import OpenInNew from "@mui/icons-material/OpenInNew";
import { Link, Stack, Typography } from "@mui/material";
import { buildStateRegistrationUrl, buildWasteStreamUrl } from "util/wasteUrls";

export type LinkedWasteStreamSummary = { id: string; name: string };

export type LinkedRegistrationSummary = {
  id: string;
  state: string;
  name?: string | null;
  stateRegisteredId?: string | null;
};

/** One new-tab link plus the detail that identifies which record it points at. */
function RecordLink({ href, label, detail }: { href: string; label: string; detail: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        variant="body2"
        underline="hover"
        sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, wordBreak: "break-word" }}
      >
        {label}
        <OpenInNew sx={{ fontSize: 16 }} />
      </Link>
      {detail && (
        <Typography variant="body2" color="text.secondary">
          {detail}
        </Typography>
      )}
    </Stack>
  );
}

function LinkGroup({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      {children}
    </Stack>
  );
}

/**
 * The waste streams and state registrations a submission is linked to, as
 * new-tab links. A registration submission asks the state to change a record
 * that lives outside the submission, so staff need to reach that record from
 * here — and, once the state responds, to add the codes to it by hand.
 *
 * Renders nothing when a submission has neither link, which is every submission
 * type that is not a waste stream registration.
 */
export function SubmissionLinkedRecords({
  tenantId,
  wasteStreams,
  registrations,
}: {
  tenantId: string;
  wasteStreams: LinkedWasteStreamSummary[];
  registrations: LinkedRegistrationSummary[];
}) {
  if (!tenantId || (wasteStreams.length === 0 && registrations.length === 0)) return null;

  return (
    <Stack spacing={2}>
      {registrations.length > 0 && (
        <LinkGroup
          label={registrations.length === 1 ? "State registration" : "State registrations"}
        >
          {registrations.map((registration) => (
            <RecordLink
              key={registration.id}
              href={buildStateRegistrationUrl(tenantId, registration.id)}
              label={registration.name || registration.stateRegisteredId || registration.state}
              detail={[registration.state, registration.stateRegisteredId]
                .filter(Boolean)
                .join(" · ")}
            />
          ))}
        </LinkGroup>
      )}
      {wasteStreams.length > 0 && (
        <LinkGroup label={wasteStreams.length === 1 ? "Waste stream" : "Waste streams"}>
          {wasteStreams.map((stream) => (
            <RecordLink
              key={stream.id}
              href={buildWasteStreamUrl(tenantId, stream.id)}
              label={stream.name}
              detail=""
            />
          ))}
        </LinkGroup>
      )}
    </Stack>
  );
}
